import {ActionList, ActionMenu, Button} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'

import {useSearchParams} from '@github-ui/use-navigate'

import type {AgentFinderResourceType} from '../../../../types'

import {getMediaTypeLabel, normalizeMediaTypes} from '../../media-types'

const RESOURCE_TYPE_FILTER_PARAM = 'type'

const FALLBACK_TYPES: AgentFinderResourceType[] = [
  {type: 'application/mcp-server+json', count: 0},
  {type: 'application/agent-skills+md', count: 0},
  {type: 'application/agents+md', count: 0},
  {type: 'application/a2a-agent-card+json', count: 0},
]

export function getSelectedResourceTypes(searchParams: URLSearchParams): string[] {
  const raw = searchParams.get(RESOURCE_TYPE_FILTER_PARAM) || ''
  const values = raw
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)

  return normalizeMediaTypes(values)
}

type Props = {
  resourceTypes: AgentFinderResourceType[]
}

export function ResourceTypeFilter({resourceTypes}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const selected = getSelectedResourceTypes(searchParams)

  const discoveredTypes = resourceTypes.length > 0 ? resourceTypes : FALLBACK_TYPES
  const discoveredValues = new Set(discoveredTypes.map(option => option.type))
  const selectedUndiscoveredTypes = selected.filter(type => !discoveredValues.has(type)).map(type => ({type, count: 0}))
  const options = [...discoveredTypes, ...selectedUndiscoveredTypes]
  const labelCounts = new Map<string, number>()
  for (const option of options) {
    const label = getMediaTypeLabel(option.type)
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1)
  }
  const countedTypes = new Set(resourceTypes.map(option => option.type))

  const handleSelect = (event: React.MouseEvent | React.KeyboardEvent, value: string) => {
    // Prevent ActionList.Item's afterSelect from closing the menu so multiple
    // types can be toggled in a single interaction.
    event.preventDefault()

    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]

    const params = new URLSearchParams(searchParams)
    if (next.length > 0) {
      params.set(RESOURCE_TYPE_FILTER_PARAM, next.join(','))
    } else {
      params.delete(RESOURCE_TYPE_FILTER_PARAM)
    }
    // Reset to the first page so the filtered view starts from the top.
    params.delete('page')

    setSearchParams(params, {preventAutofocus: true})
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          trailingVisual={TriangleDownIcon}
          count={selected.length || undefined}
          data-testid="resource-type-filter"
        >
          Resource type
        </Button>
      </ActionMenu.Anchor>

      <ActionMenu.Overlay width="auto">
        <ActionList selectionVariant="multiple" aria-label="Filter by resource type">
          {options.map(option => {
            const label = getMediaTypeLabel(option.type)
            const optionLabel = labelCounts.get(label) === 1 ? label : `${label} (${option.type})`
            const value = option.type

            return (
              <ActionList.Item
                key={option.type}
                selected={selected.includes(value)}
                onSelect={event => handleSelect(event, value)}
              >
                {optionLabel}
                {countedTypes.has(option.type) && (
                  <ActionList.TrailingVisual>{option.count.toLocaleString()}</ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
